import { injectable } from 'inversify';
import type { CartRepository } from '../../domain/repositories/SaleRepository';
import { Cart, CartItem } from '../../domain/models';

/**
 * Database record interface for Cart
 */
interface CartRecord {
  id: string;
  _id: string;
  branchId: string;
  userId: string;
  items: CartItem[];
  discountPercentage: number;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  customerId?: string;
  status: 'active' | 'completed' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Implementation of CartRepository using DatabaseDataSource
 * Currently uses in-memory storage for demo purposes
 */
@injectable()
export class CartRepositoryImpl implements CartRepository {
  // In-memory storage for demo (in production, this would use database)
  private carts: Map<string, CartRecord> = new Map();

  constructor() {
    // Note: Using in-memory storage for now, will migrate to database later
  }

  async create(cartData: Omit<Cart, 'id' | 'createdAt' | 'updatedAt'>): Promise<Cart> {
    const now = new Date();
    const cartId = `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const cartRecord: CartRecord = {
      ...cartData,
      id: cartId,
      _id: cartId,
      userId: 'default_user', // TODO: Get from context
      branchId: 'branch_qalam_benghazi', // TODO: Get from context
      status: 'active',
      createdAt: now,
      updatedAt: now,
      // syncStatus: 'pending' // This field is no longer used
    };

    this.carts.set(cartId, cartRecord);
    console.log(`Cart created: ${cartId}`);
    
    return this.mapToCart(cartRecord);
  }

  async findById(id: string): Promise<Cart | null> {
    const cartRecord = this.carts.get(id);
    return cartRecord ? this.mapToCart(cartRecord) : null;
  }

  async update(id: string, cartData: Partial<Cart>): Promise<Cart> {
    const cartRecord = this.carts.get(id);
    if (!cartRecord) {
      throw new Error(`Cart with id ${id} not found`);
    }

    // Update the record
    Object.assign(cartRecord, {
      ...cartData,
      updatedAt: new Date(),
      // syncStatus: 'pending' // This field is no longer used
    });

    // Recalculate totals if items were updated
    if (cartData.items) {
      this.recalculateTotals(cartRecord);
    }

    this.carts.set(id, cartRecord);
    console.log(`Cart updated: ${id}`);
    
    return this.mapToCart(cartRecord);
  }

  async delete(id: string): Promise<void> {
    const cartRecord = this.carts.get(id);
    if (!cartRecord) {
      throw new Error(`Cart with id ${id} not found`);
    }

    // Mark as cancelled instead of deleting
    cartRecord.status = 'cancelled';
    cartRecord.updatedAt = new Date();
    // cartRecord.syncStatus = 'pending'; // This field is no longer used
    
    this.carts.set(id, cartRecord);
    console.log(`Cart cancelled: ${id}`);
  }

  async findActiveByUser(userId: string): Promise<Cart[]> {
    const activeCarts: Cart[] = [];
    
    for (const cartRecord of this.carts.values()) {
      if (cartRecord.userId === userId && cartRecord.status === 'active') {
        activeCarts.push(this.mapToCart(cartRecord));
      }
    }
    
    return activeCarts;
  }

  /**
   * Recalculate cart totals based on items
   */
  private recalculateTotals(cartRecord: CartRecord): void {
    let subtotal = 0;

    // Calculate subtotal from items
    cartRecord.items.forEach(item => {
      subtotal += item.subtotal;
    });

    cartRecord.subtotal = subtotal;
    cartRecord.discountAmount = (subtotal * cartRecord.discountPercentage) / 100;
    cartRecord.totalAmount = subtotal - cartRecord.discountAmount;
  }

  /**
   * Map database record to domain model
   */
  private mapToCart(record: CartRecord): Cart {
    return {
      id: record.id,
      branchId: record.branchId,
      userId: record.userId,
      items: record.items,
      discountPercentage: record.discountPercentage,
      subtotal: record.subtotal,
      discountAmount: record.discountAmount,
      totalAmount: record.totalAmount,
      customerId: record.customerId,
      createdAt: record.createdAt || new Date(),
      updatedAt: record.updatedAt || new Date()
    };
  }
} 